/* @flow */

import { Formik, Form, Field } from 'formik';
import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import * as Yup from 'yup';
import { B2MB } from '@e180/util/lib/unit';

import styles from './SupportForm.style';

import SupportPictureUploadButton from './SupportPictureUploadButton';
import messages from 'src/shared/app/support/l10n/supportL10n';
import {
  technicalSupport,
  generalSupport,
  feedback,
  other,
  maxFileSize,
} from 'src/shared/app/support/settings/supportSettings';
import pictureMessages from 'src/shared/app/account/profile/l10n/pictureUploadL10n';
import FormikFormGroup from 'src/shared/app/base/component/data-entry/form/FormikFormGroup';
import LazyFAQMain from 'src/shared/app/faq/component/LazyFAQMain';
import SecondaryButton from 'src/shared/ui/component/button/SecondaryButton';
import RadioButtonList from 'src/shared/ui/component/form/RadioButtonList';
import InstructionText from 'src/shared/ui/component/text/InstructionText';

type Props = {
  handleSubmit: (Object) => Promise<any>,
};

const useStyles = createUseStyles(styles);

const SupportFormDumb = ({ handleSubmit }: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();

  const schema = Yup.object()
    .shape({
      support_type: Yup.string().required(),
      message: Yup.string().required(),
    })
    .test({
      name: 'imageSize',
      test: ({ image }) => {
        if (image) {
          const imageSize = image.size ? B2MB(image.size) : null;

          if (imageSize && imageSize > maxFileSize) {
            // $FlowIssue
            const error = new Yup.ValidationError(
              intl.formatMessage(pictureMessages.sizeError, {
                size: imageSize,
                maxSize: maxFileSize,
              }),
            );

            error.path = 'image';

            return error;
          }
        }

        return true;
      },
    });

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onMouseOver = () => LazyFAQMain.preload();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  return (
    <Formik
      initialValues={{
        support_type: '',
        message: '',
        image: null,
      }}
      validationSchema={schema}
      validateOnBlur={true}
      onSubmit={handleSubmit}
    >
      {({ dirty }) => (
        <Form className={classes.root} noValidate>
          <Field
            id="support_type"
            name="support_type"
            component={FormikFormGroup}
            componentOpts={{
              inputComp: RadioButtonList,
              inputOpts: {
                data: [
                  {
                    value: technicalSupport,
                    text: intl.formatMessage(messages.supportTypeTechnical),
                  },
                  {
                    value: generalSupport,
                    text: intl.formatMessage(messages.supportTypeGeneral),
                  },
                  {
                    value: feedback,
                    text: intl.formatMessage(messages.supportTypeFeedback),
                  },
                  {
                    value: other,
                    text: intl.formatMessage(messages.supportTypeOther),
                  },
                ],
              },
              label: intl.formatMessage(messages.supportTypeTitle),
            }}
          />
          <Field
            id="message"
            name="message"
            component={FormikFormGroup}
            componentOpts={{
              useTextarea: true,
              isHighlighted: true,
              label: intl.formatMessage(messages.userCommentTitle),
            }}
            rows="3"
            placeholder={intl.formatMessage(messages.userCommentPlaceholder)}
          />

          <InstructionText layoutClass={classes.instructions}>
            {intl.formatMessage(messages.userScreenshotNote)}
          </InstructionText>
          <div className={classes.imageSection}>
            <Field
              id="image"
              name="image"
              component={SupportPictureUploadButton}
            />
          </div>

          <div className={classes.sendButton}>
            <SecondaryButton
              isDisabled={!dirty}
              type="submit"
              handleMouseOver={onMouseOver}
            >
              {intl.formatMessage(messages.supportSendButton)}
            </SecondaryButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default compose(React.memo)(SupportFormDumb);
